<script setup lang="ts">
import { SUPPORTED_MIME_TYPES, type SupportedMimeType } from '@/backend/types'
import FileDropIndicator from '@/modules/Workspaces/KnowledgeHub/Files/FileDropIndicator.vue'
import { getFilesFromDataTransferEvent, type FileAndPath } from '@/shared/utils/file'
import { useDropZone } from '@vueuse/core'
import { ref, useTemplateRef } from 'vue'

const emit = defineEmits<{
  drop: [files: FileAndPath[]]
}>()
// Hub-specific MIME types that include XLSX support
const xlsxMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const HUB_SUPPORTED_MIME_TYPES = [...SUPPORTED_MIME_TYPES, xlsxMimeType]

const dropzone = useTemplateRef('dropzone')
const draggedFiles = ref<{ type: SupportedMimeType }[]>([])

const { isOverDropZone } = useDropZone(dropzone, {
  multiple: true,
  preventDefaultForUnhandled: true,
  // No data type enforcement here because it would prevent dropping folders
  // dataTypes: supportedMimeTypes,
  async onEnter(files, event) {
    // We can't read folders here, browser won't let you do that before you drop them.
    const allItems = [...(event.dataTransfer?.items || [])]

    draggedFiles.value = allItems
      .filter(
        (item) => item.kind === 'file' && HUB_SUPPORTED_MIME_TYPES.some((t) => t === item.type),
      )
      .map(({ type }) => ({ type: type as SupportedMimeType }))
  },

  async onDrop(_, event) {
    const allFiles = await getFilesFromDataTransferEvent(event)
    const filteredFiles = allFiles.filter(({ file }) =>
      HUB_SUPPORTED_MIME_TYPES.some((t) => t === file.type),
    )
    const dedupedFiles = dedupeFiles(filteredFiles)

    emit('drop', dedupedFiles)
  },
})

/**
 * When you select a range in Finder such as
 * - Folder 1
 * - - Folder 2
 * - - - File 1
 *
 * And then drag & drop it, you will get duplicates.
 * Folder 1 will be recursively scanned, but Folder 2 also made the selection, so it will be scanned separately again.
 * File 1 will be uploaded twice.
 *
 * We should catch this.
 * It seems like a safe bet to deduplicate files by keeping ones with the longest full path.
 *
 *  This is an O^2 function and should not be in a tight loop.
 */
function dedupeFiles(fnps: FileAndPath[]): FileAndPath[] {
  const dupes = new Set<FileAndPath>()

  for (const currentFile of fnps) {
    for (const checkedFile of fnps) {
      if (
        currentFile !== checkedFile &&
        checkedFile.fullPath.endsWith(currentFile.fullPath) &&
        filesMightBeSame(currentFile.file, checkedFile.file)
      ) {
        dupes.add(currentFile)
      }
    }
  }

  return fnps.filter((item) => !dupes.has(item))
}

function filesMightBeSame(a: File, b: File): boolean {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified
}
</script>

<template>
  <div
    ref="dropzone"
    class="size-full"
  >
    <slot :props="{ isOverDropZone }" />

    <FileDropIndicator
      :show="isOverDropZone"
      class="pointer-events-none absolute inset-0 size-full"
      :files="draggedFiles"
    />
  </div>
</template>
